/**
 * Shared utility functions for filter state management.
 */

/**
 * Converts draft filter values into the shape the API expects for persistence.
 * Filters at full range (min/max equals the property bounds) are omitted —
 * they represent "no limit".
 *
 * @param {object} draftValues - { [filterId]: { min, max } }
 * @param {Array<{ id: string, min: number, max: number }>} configs - Filter configs with bounds
 * @returns {object|null} - Filter payload for the API, or null if no active filters
 */
export const buildFilterPayload = (draftValues, configs) => {
	const payload = {};
	let hasActive = false;

	for (const config of configs) {
		if (config.disabled) continue;
		const value = draftValues[config.id];
		if (!value) continue;

		// Coalesce null to bound — handles transient null from cleared InputNumber fields
		const selection = { min: value.min ?? config.min, max: value.max ?? config.max };
		if (isFilterActive(selection, config)) {
			payload[config.id] = selection;
			hasActive = true;
		}
	}

	return hasActive ? payload : null;
};

/**
 * Computes a client-side preview count by estimating how much of the data
 * range the current filter selection covers. No API call — purely local.
 *
 * Treats each filter's range reduction as an independent multiplicative factor.
 * This is a rough estimate for preview purposes.
 *
 * @param {object} draftValues - { [filterId]: { min, max } }
 * @param {Array<{ id: string, min: number, max: number, disabled?: boolean }>} configs
 * @param {number} totalCount - Total objects
 * @returns {number}
 */
export const computePreviewCount = (draftValues, configs, totalCount) => {
	if (!configs.length || !totalCount) return totalCount;

	let ratio = 1;
	for (const config of configs) {
		if (config.disabled) return 0;
		const value = draftValues[config.id];
		if (!value) continue;
		const fullRange = config.max - config.min;
		if (fullRange <= 0) continue;
		// Coalesce null to bound — handles transient null from cleared InputNumber fields
		const selectedMin = value.min ?? config.min;
		const selectedMax = value.max ?? config.max;
		const selectedSpan = selectedMax - selectedMin;
		ratio *= Math.max(0, Math.min(1, selectedSpan / fullRange));
	}

	return Math.max(0, Math.round(totalCount * ratio));
};

/**
 * A filter is "active" when its selection differs from the bounds it's measured
 * against — a bound left at the range edge isn't a filter.
 *
 * Which bounds matter: a collection filter uses its own range, a per-object one
 * must use its effective config (see `computeEffectiveFilters`).
 *
 * @param {{ min: number, max: number }|undefined} value
 * @param {{ min: number, max: number }|undefined} config - bounds to compare against
 * @returns {boolean}
 */
export const isFilterActive = (value, config) => {
	if (!value || !config) return false;
	return value.min !== config.min || value.max !== config.max;
};

/**
 * A config's own selection, with `null` ("no limit") resolved to the bound.
 * The filter forms seed from this too.
 *
 * @param {{ min: number, max: number, selectedMin: ?number, selectedMax: ?number }} config
 * @returns {{ min: number, max: number }}
 */
export const selectedRange = config => ({
	min: config.selectedMin ?? config.min,
	max: config.selectedMax ?? config.max,
});

/**
 * How many of these configs narrow their range.
 *
 * Pass *effective* configs for per-object filters — against an object's raw
 * bounds, a selection the collection filter already enforces counts as active
 * and the row disagrees with the panel.
 *
 * @param {Array} configs
 * @returns {number}
 */
export const countActiveFilters = configs =>
	(configs ?? []).filter(config => isFilterActive(selectedRange(config), config)).length;
